// @flow
import type { RequestStates } from 'types/redux';
import type { Errors } from 'types/errors';
import type { ActivePassengerBooking } from 'types/dashboard';
import type { Booking, BookingRequest } from 'types/bookings';
import type {
  Cohort,
  Driver,
  Latitude,
  Longitude,
  Location,
  Passengers,
} from 'types/entities';
import type { MapMode } from 'components/map/Map';

import _ from 'lodash';
import React, { Component, PropTypes } from 'react';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Link } from 'react-router';
import grabStorage from 'grab-storage';
import Tooltip from 'rc-tooltip';
import { Map as IMap } from 'immutable';
import autobind from 'react-autobind';

import Config from 'config';
import {
  cohortCreate,
  cohortGet,
  cohortDelete,
  unsetCohort,
} from 'actions/cohorts';
import { driverSetLocationInstant, driverSetLocation } from 'actions/drivers';
import { passengerBookingCreate, passengerGetNearbyTaxiTypes } from 'actions/passengers';
import { resetErrorState } from 'actions/helpers';
import {
  unsetNewlyCreatedBooking,
  unsetActiveBooking,
  driverDrag,
  reverseGeo,
} from 'actions/app';

import { roundCoordinate } from 'utils/map-utils';
import { DEFAULT_PAYMENT_TYPE, DEFAULT_REWARD_TYPE } from 'utils/library-utils';
import { BOOKING_MARKER_PICKUP } from 'types/bookings';

/* eslint-disable import/no-named-as-default-member */
import Map from 'components/map/Map';
import ErrorsNotification from 'components/others/ErrorNotification';

import PassengersPanel from './passengers/PassengersPanel';
import DriversPanel from './drivers/DriversPanel';
import CreateBookingPanel from './bookings/CreateBookingPanel';
import BookingDetailsPanel from './bookings/BookingDetailsPanel';
import handleCohortEvent from './cohort-events-handler';

import './DashboardContainer.scss';
import './EntitiesPanel.scss';

const taxiTypeIDKey = 'taxiTypeID';
const paymentTypeKey = 'paymentType';
const requestErrors = [
  'cohortCreate',
  'cohortGet',
  'cohortDelete',
];

type Props = {
  unsetCohort: Function,
  activeCohortID: string,
  activeCohort: Cohort,
  driverBeingDragged: ?string,

  passengers: Passengers,

  cohortCreate: Function,
  cohortCreateRequest: RequestStates,
  cohortCreateErrors: Errors,

  cohortGet: Function,
  cohortGetRequest: RequestStates,
  cohortGetErrors: Errors,

  cohortDelete: Function,
  cohortDeleteRequest: RequestStates,
  cohortDeleteErrors: Errors,

  driverSetLocation: Function,
  driverSetLocationInstant: Function,

  driverDrag: Function,

  passengerBookingCreate: Function,
  passengerBookingCreateRequest: RequestStates,
  passengerBookingCreateErrors: Errors,

  newlyCreatedBookingID: ?string,
  unsetNewlyCreatedBooking: Function,

  appActiveBooking: Booking,
  unsetActiveBooking: Function,

  resetErrorState: Function,

  passengerGetNearbyTaxiTypes: Function,
  passengerGetNearbyTaxiTypesRequest: RequestStates,
  passengerGetNearbyTaxiTypesErrors: Errors,
  nearbyTaxiTypes: ?Array<any>,

  reverseGeo: Function,
  pois: IMap,
};

type State = {
  searchCohortID: string,
  activePassengerBooking: ?ActivePassengerBooking,
  cohortDisconnectionCode: ?number,
  cohortPaxType: number,
  newCohortName: string,
};

export class DashboardContainer extends Component {
  // Can't get context to work with Flow. Fallback to es7 approach.
  static contextTypes = {
    store: PropTypes.object,
    router: PropTypes.object,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      searchCohortID: '',
      newCohortName: '',
      activePassengerBooking: null,
      cohortDisconnectionCode: null,
      cohortPaxType: 0,
    };
    this.cohortSocket = null;
    autobind(this, 'renderBookingPanel');
  }

  state: State;

  componentDidMount() {
    this.props.resetErrorState(requestErrors);
    if (this.props.activeCohortID) {
      this.props.cohortGet(this.props.activeCohortID);
      this.context.router.push({
        pathname: `/cohorts/${this.props.activeCohortID}`,
        state: {
          cohortRedirection: true,
        },
      });
    }
  }

  componentWillUpdate(nextProps: Props) {
    const appActiveBookingChanged: boolean = !_.isEqual(this.props.appActiveBooking,
      nextProps.appActiveBooking) && !!nextProps.appActiveBooking;
    const activeCohortIDChanged: boolean = this.props.activeCohortID !== nextProps.activeCohortID;
    const successfulCohortGetRequest: boolean = !this.props.cohortGetRequest.isSuccessful &&
      nextProps.cohortGetRequest.isSuccessful;
    const successfulCohortCreateRequest: boolean = !this.props.cohortCreateRequest.isSuccessful &&
      nextProps.cohortCreateRequest.isSuccessful;
    const successfulCohortDeleteRequest: boolean = !this.props.cohortDeleteRequest.isSuccessful &&
      nextProps.cohortDeleteRequest.isSuccessful;
    const activePassengerRemoved: boolean = !!this.state.activePassengerBooking &&
      !_.has(nextProps, `passengers.${this.state.activePassengerBooking.passengerID}`);
    const activePassengerLoggedOut: boolean = !!this.state.activePassengerBooking &&
      _.has(nextProps, `passengers.${this.state.activePassengerBooking.passengerID}`) &&
      !nextProps.passengers[this.state.activePassengerBooking.passengerID].loggedIn;

    if (activeCohortIDChanged) {
      if (nextProps.activeCohortID) {
        this.context.router.push(`/cohorts/${nextProps.activeCohortID}`);
      } else {
        this.context.router.push('/app');
      }
    }

    if (appActiveBookingChanged ||
      activeCohortIDChanged ||
      successfulCohortGetRequest ||
      successfulCohortCreateRequest ||
      successfulCohortDeleteRequest ||
      activePassengerRemoved ||
      activePassengerLoggedOut) {
      this.setState({
        activePassengerBooking: null,
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const successfulCohortGetRequest: boolean = !prevProps.cohortGetRequest.isSuccessful &&
      this.props.cohortGetRequest.isSuccessful;
    const successfulCohortCreateRequest: boolean = !prevProps.cohortCreateRequest.isSuccessful &&
      this.props.cohortCreateRequest.isSuccessful;
    const successfulCohortDeleteRequest: boolean = !prevProps.cohortDeleteRequest.isSuccessful &&
      this.props.cohortDeleteRequest.isSuccessful;
    if (successfulCohortGetRequest || successfulCohortCreateRequest) {
      this.connectActiveCohortSocket();
    }
    if (successfulCohortDeleteRequest) {
      this.disconnectActiveCohortSocket();
    }

    if ((prevProps.passengerBookingCreateErrors.isFailure &&
      !this.props.passengerBookingCreateErrors.isFailure)) {
      this.props.resetErrorState('passengerBookingCreate');
    }
  }

  componentWillUnmount() {
    this.disconnectActiveCohortSocket();
  }

  connectActiveCohortSocket() {
    if (this.cohortSocket) {
      this.cohortSocket.close();
    }
    const cohortID: string = this.props.activeCohortID;
    const cohortSocket = new WebSocket(`${Config.WEBSOCKET_BASE_URL}/cohorts/${cohortID}/events`);

    cohortSocket.onmessage = (event: any) => {
      const action = handleCohortEvent(cohortID, JSON.parse(event.data));
      this.context.store.dispatch(action);
    };
    cohortSocket.onclose = (event: any) => {
      if (this.cohortSocket !== cohortSocket) {
        return;
      }
      this.setState({
        cohortDisconnectionCode: event.code,
      });
      this.disconnectActiveCohortSocket();
    };
    this.cohortSocket = cohortSocket;
  }

  disconnectActiveCohortSocket() {
    if (this.cohortSocket) {
      this.cohortSocket.onclose = null;
      this.cohortSocket.close();
      this.cohortSocket = null;
    }
  }

  cohortSocket: ?Object;
  map: Object;
  props: Props;

  renderBookingPanel(activePassengerBooking: ActivePassengerBooking) {
    if (this.props.activeCohortID && activePassengerBooking.passengerID) {
      const {
        passengerBookingCreate: createBooking,
        passengerBookingCreateRequest,
        resetErrorState: resetError,
        newlyCreatedBookingID,
        unsetNewlyCreatedBooking: resetBookingState,
        activeCohortID,
        passengerBookingCreateErrors,
        nearbyTaxiTypes,
        passengerGetNearbyTaxiTypesRequest,
        passengerGetNearbyTaxiTypesErrors,
        pois,
        reverseGeo: getPOI,
      } = this.props;

      return (
        <CreateBookingPanel
          activePassengerBooking={activePassengerBooking}
          onUpdate={(booking: ActivePassengerBooking) => {
            this.setState({
              activePassengerBooking: booking,
            });
          }}
          onCreate={(bookingRequest: BookingRequest) => {
            const cohortID: string = activeCohortID;
            const passengerID: string = activePassengerBooking.passengerID;
            // TODO: Potentially reset the cached TaxiTypeID if cohort changes.
            grabStorage.setItem(taxiTypeIDKey, activePassengerBooking.taxiTypeID);
            grabStorage.setItem(paymentTypeKey, activePassengerBooking.paymentType);
            createBooking(cohortID, passengerID, bookingRequest, pois);
          }}
          onShow={() => {
            resetError('passengerBookingCreate');
          }}
          onHide={() => {
            resetBookingState();
          }}
          isLoading={passengerBookingCreateRequest.isPending}
          successBookingID={newlyCreatedBookingID}
          errors={passengerBookingCreateErrors}
          handleClose={() => {
            this.setState({
              activePassengerBooking: null,
            });
          }}
          nearbyTaxiTypes={nearbyTaxiTypes}
          isGetTaxiTypesPending={passengerGetNearbyTaxiTypesRequest.isPending}
          taxiTypesErrors={passengerGetNearbyTaxiTypesErrors}
          pointOfInterests={pois}
          getPOI={(location: Location) => {
            getPOI(location);
          }}
          clearError={() => {
            resetError('passengerBookingCreate');
          }}
        />
      );
    }

    return null;
  }

  renderMap(pickUpLocation: ?Location, dropOffLocation: ?Location,
    extraDropOffLocations: ?Array<Location>, activePassengerBooking: Object, bookingMode: ?MapMode) {
    const {
      activeCohort,
      activeCohortID,
      driverSetLocationInstant: localSetDriverLocation,
      driverSetLocation: remoteSetDriverLocation,
      driverDrag: handleDriverDrag,
      driverBeingDragged,
      reverseGeo: handleReverseGeo,
      passengerGetNearbyTaxiTypes: handleGetNearbyTaxiTypes,
    } = this.props;
    return (
      <Map
        accessToken={Config.MAPBOX_TOKEN}
        mapboxStyle={Config.MAP_STYLE}
        cohort={activeCohort}
        pickUpMarker={pickUpLocation}
        dropOffMarker={dropOffLocation}
        extraDropOffMarkers={extraDropOffLocations}
        ref={(map: Object) => { this.map = map; }}
        onDriverDrag={(driverID: string, location: Location) => {
          localSetDriverLocation(activeCohortID, driverID, location, true);
        }}
        onDriverDragStart={(driverID: string) => {
          handleDriverDrag(driverID);
        }}
        onDriverDragEnd={(driverID: string, location: Location) => {
          handleDriverDrag(null);
          remoteSetDriverLocation(activeCohortID, driverID, location);
        }}
        mapDragEnd={() => {
          if (driverBeingDragged) {
            // Reset if there's a driver being dragged.
            handleDriverDrag(null);
          }
        }}
        onBookingMarkerDrag={(type: string, location: Location) => {
          const locationType = {
            pickUp: 'pickUpLocation',
            dropOff: 'dropOffLocation',
            extraDropOff_0: 0,
          }[type];

          if (typeof locationType === 'number') {
            this.setState((curState) => {
              const paxBkg = curState.activePassengerBooking;
              const updatedLocations = _.slice(paxBkg.extraDropOffLocations);
              updatedLocations[locationType] = location.map(value => roundCoordinate(value));

              return {
                activePassengerBooking: {
                  ...paxBkg,
                  extraDropOffLocations: updatedLocations,
                },
              };
            });
          } else {
            this.setState({
              activePassengerBooking: {
                ...activePassengerBooking,
                [locationType]: location.map(value => roundCoordinate(value)),
              },
            });
          }
        }}
        onBookingMarkerDragEnd={(location: Location, bookingMarkerType: number) => {
          handleReverseGeo(location.map(value => roundCoordinate(value)));
          if (bookingMarkerType === BOOKING_MARKER_PICKUP) {
            handleGetNearbyTaxiTypes(activePassengerBooking.passengerID, location);
          }
        }}
        mode={bookingMode}
      />
    );
  }

  render() {
    const activePassengerBooking: Object = this.state.activePassengerBooking || {};
    let bookingMode: ?MapMode = null;
    let pickUpLocation: ?Location = null;
    let dropOffLocation: ?Location = null;
    let itinerary: ?Array<Object> = null;
    let extraDropOffLocations: Array<Location> = [];

    if (!_.isEmpty(activePassengerBooking)) {
      bookingMode = 'BOOKING';
      pickUpLocation = activePassengerBooking.pickUpLocation;
      dropOffLocation = activePassengerBooking.dropOffLocation;
      extraDropOffLocations = activePassengerBooking.extraDropOffLocations;
    } else if (this.props.appActiveBooking) {
      const appActiveBooking: Booking = this.props.appActiveBooking;
      itinerary = appActiveBooking.itinerary;
      bookingMode = 'DISPLAY_BOOKING';

      const pickUpPoint: Object = _.first(itinerary);
      const dropOffPoint: Object = _.last(itinerary);
      if (pickUpPoint.longitude && pickUpPoint.latitude) {
        pickUpLocation = [pickUpPoint.longitude, pickUpPoint.latitude];
      }
      if (dropOffPoint.longitude && dropOffPoint.latitude) {
        dropOffLocation = [dropOffPoint.longitude, dropOffPoint.latitude];
      }
      if (_.size(itinerary) > 2) {
        const [, ...rest] = itinerary;
        extraDropOffLocations = _.take(rest, rest.length - 1).map(point => [point.longitude, point.latitude]);
      }
    }

    return (
      <DocumentTitle title="Marionette">
        <div className="map-page">
          <div className="map-page-content columns is-gapless">
            <div className="column is-one-third map-sidebar-container">
              <div className="map-sidebar">
                <div className="cohort-selection-panel map-sidebar-section">
                  <h3 className="title section-title is-6">Cohort{' '}
                    <Tooltip
                      placement="right"
                      trigger="hover"
                      overlay={
                        <span>
                          Cohort drivers will only be able to receive<br />
                          jobs created within the same cohort.
                        </span>
                      }
                    >
                      <i className="fa fa-question-circle" />
                    </Tooltip>
                  </h3>
                  <div className="columns">
                    <div className="column is-full">
                      {this.props.activeCohortID &&
                        <div className="cohort-tags">
                          <div className="tags has-addons">
                            <span className="tag">Passenger Type</span>
                            <span className="tag is-primary is-small">
                              {this.props.activeCohort.paxType === 0 ? 'PASSENGER' : 'CHARICE'}
                            </span>
                          </div>
                        </div>
                      }
                      <div className="active-cohort columns">
                        <form
                          className="field has-addons column is-three-quarters"
                          onSubmit={(event) => {
                            event.preventDefault();
                            this.props.cohortGet(this.state.searchCohortID);
                            this.props.resetErrorState(requestErrors);
                          }}
                        >
                          <p className="control is-full-width">
                            {this.props.activeCohortID ?
                              <input
                                className="input"
                                type="text"
                                disabled
                                value={this.props.activeCohortID}
                                aria-label="Cohort ID"
                              />
                              :
                              <input
                                className="input"
                                type="text"
                                placeholder="Cohort Search: Enter cohort ID"
                                value={this.state.searchCohortID}
                                aria-label="Cohort ID"
                                onChange={(event) => {
                                  this.setState({
                                    searchCohortID: event.target.value,
                                  });
                                }}
                              />
                            }
                          </p>
                          <p className="control">
                            {this.props.activeCohortID ?
                              <button
                                className={classnames('button is-danger', {
                                  'is-loading': this.props.cohortDeleteRequest.isPending,
                                })}
                                title="Delete cohort"
                                aria-label="Delete cohort"
                                type="button"
                                onClick={() => {
                                  /* eslint-disable no-alert */
                                  if (confirm(`Are you sure you want to delete Cohort ${this.props.activeCohortID}?`)) {
                                    this.props.cohortDelete(this.props.activeCohortID);
                                    this.props.resetErrorState(requestErrors);
                                    this.setState({
                                      searchCohortID: '',
                                    });
                                  }
                                }}
                              >
                                <span className="icon" aria-hidden="true">
                                  <i className="fa fa-trash" />
                                </span>
                              </button>
                              :
                              <button
                                className={classnames('button is-info', {
                                  'is-loading': this.props.cohortGetRequest.isPending,
                                  'is-disabled': !this.state.searchCohortID,
                                })}
                                title="Select cohort"
                                aria-label="Select cohort"
                                type="submit"
                              >
                                <span className="icon" aria-hidden="true">
                                  <i className="fa fa-search" />
                                </span>
                              </button>
                            }
                          </p>
                          {this.props.activeCohortID &&
                            <p className="control">
                              <button
                                className="button is-warning"
                                title="Unselect cohort"
                                aria-label="Unselect cohort"
                                onClick={() => {
                                  this.props.unsetCohort(this.props.activeCohortID);
                                  this.setState({
                                    searchCohortID: '',
                                    newCohortName: '',
                                    cohortDisconnectionCode: null,
                                  });
                                  this.props.resetErrorState(requestErrors);
                                  this.disconnectActiveCohortSocket();
                                }}
                              >
                                <span className="icon" aria-hidden="true">
                                  <i className="fa fa-times" />
                                </span>
                              </button>
                            </p>
                          }
                        </form>
                      </div>
                    </div>
                  </div>

                  <Link
                    className="generate-resource-link"
                    to="/generate-resources"
                    title="Generate Resources"
                    aria-label="Generate Resources"
                  >
                    <h3>Generate new cohort</h3>
                  </Link>
                  {this.state.cohortDisconnectionCode &&
                    <div className="notification is-danger" role="alert">
                      Cohort socket disconnected. Error code: {this.state.cohortDisconnectionCode}
                      <button
                        className="button is-small is-pulled-right" onClick={() => {
                          this.props.cohortGet(this.props.activeCohortID);
                        }}
                      >
                        Reconnect
                      </button>
                    </div>
                  }
                  {!_.isEmpty(this.props.cohortCreateErrors) &&
                    <ErrorsNotification
                      errors={this.props.cohortCreateErrors}
                      dismissible
                      onClose={() => {
                        this.props.resetErrorState('cohortCreate');
                      }}
                    />
                  }
                  {!_.isEmpty(this.props.cohortGetErrors) &&
                    <ErrorsNotification
                      errors={this.props.cohortGetErrors}
                      dismissible
                      onClose={() => {
                        this.props.resetErrorState('cohortGet');
                      }}
                    />
                  }
                  {!_.isEmpty(this.props.cohortDeleteErrors) &&
                    <ErrorsNotification
                      errors={this.props.cohortDeleteErrors}
                      dismissible
                      onClose={() => {
                        this.props.resetErrorState('cohortDelete');
                      }}
                    />
                  }
                </div>
                {!this.props.activeCohortID &&
                  <div className="map-sidebar-section">
                    <div className="notification is-warning" role="alert">
                      No cohort selected for viewing. Please enter a cohort ID above.
                    </div>
                  </div>
                }
                {this.props.activeCohortID &&
                  <div className="cohort-passengers map-sidebar-section">
                    <PassengersPanel
                      bookingInProgress={activePassengerBooking.passengerID}
                      onPassengerBookingClick={(passengerID: string) => {
                        // For some reason DimensionsHOC.getWrappedInstance does not work.
                        const mapCenter: { latitude: Latitude, longitude: Longitude } =
                          this.map.refs.wrappedInstance.getMapCenter();
                        const lat: Latitude = roundCoordinate(mapCenter.latitude);
                        const lng: Longitude = roundCoordinate(mapCenter.longitude);

                        const {
                          unsetActiveBooking: deactivateBooking,
                          reverseGeo: getPOI,
                          passengerGetNearbyTaxiTypes: handleGetNearbyTaxiTypes,
                        } = this.props;

                        deactivateBooking();

                        const curBooking = {
                          passengerID,
                          // Just to place the markers on the map.
                          pickUpLocation: [lng - 0.02, lat + 0.01],
                          dropOffLocation: [lng + 0.02, lat + 0.01],
                          extraDropOffLocations: [[]],
                          taxiTypeID: grabStorage.getItem(taxiTypeIDKey) || '',
                          paymentType: grabStorage.getItem(paymentTypeKey) || DEFAULT_PAYMENT_TYPE,
                          rewardType: DEFAULT_REWARD_TYPE,
                          rewardValue: '',
                        };
                        this.setState({
                          activePassengerBooking: curBooking,
                        });

                        getPOI(curBooking.pickUpLocation);
                        getPOI(curBooking.dropOffLocation);
                        handleGetNearbyTaxiTypes(passengerID, curBooking.pickUpLocation);
                      }}
                    />
                  </div>
                }
                {this.props.activeCohortID &&
                  <div className="cohort-drivers map-sidebar-section">
                    <DriversPanel
                      onDriverClick={(driver: Driver) => {
                        this.map.refs.wrappedInstance.setMapCenter(driver.latitude, driver.longitude);
                      }}
                      getMapCenter={() => this.map.refs.wrappedInstance.getMapCenter()}
                    />
                  </div>
                }
              </div>
            </div>
            <div className="column is-two-third map-section">
              {this.renderMap(pickUpLocation, dropOffLocation,
                extraDropOffLocations, activePassengerBooking, bookingMode)}
              {this.renderBookingPanel(activePassengerBooking)}
              {this.props.appActiveBooking &&
                <BookingDetailsPanel
                  booking={this.props.appActiveBooking}
                  handleClose={() => {
                    this.props.unsetActiveBooking();
                  }}
                />
              }
            </div>
          </div>
        </div>
      </DocumentTitle>
    );
  }
}

function mapStateToProps(state) {
  const activeCohortID: string = state.app.activeCohortID;
  const activeBooking: Object = state.app.activeBooking;
  let appActiveBooking: ?Booking = null;

  if (activeBooking) {
    let bookings: Array<Booking> = [];
    if (activeBooking.passengerID) {
      const passengerID: string = activeBooking.passengerID;
      bookings = _.get(state.cohorts[activeCohortID], `passengers.${passengerID}.bookings`, []);
    }
    if (activeBooking.driverID) {
      const driverID: string = activeBooking.driverID;
      bookings = _.get(state.cohorts[activeCohortID], `drivers.${driverID}.bookings`, []);
    }
    appActiveBooking = _.find(bookings, (bkg: Booking) => bkg.bookingID === activeBooking.bookingID) || null;
  }

  return {
    activeCohortID,
    activeCohort: state.cohorts[activeCohortID] || {},
    driverBeingDragged: state.app.driverBeingDragged,
    passengers: _.get(state.cohorts[activeCohortID], 'passengers', {}),

    cohortCreateRequest: state.requests.cohortCreateRequest || {},
    cohortCreateErrors: _.get(state.errors, 'cohortCreate.errors', []),

    cohortGetRequest: state.requests.cohortGetRequest || {},
    cohortGetErrors: _.get(state.errors, 'cohortGet.errors', []),

    cohortDeleteRequest: state.requests.cohortDeleteRequest || {},
    cohortDeleteErrors: _.get(state.errors, 'cohortDelete.errors', []),

    passengerBookingCreateRequest: state.requests.passengerBookingCreateRequest || {},
    passengerBookingCreateErrors: _.get(state.errors, 'passengerBookingCreate.errors', []),

    newlyCreatedBookingID: state.app.newlyCreatedBookingID,
    appActiveBooking,

    nearbyTaxiTypes: state.app.nearbyTaxiTypes,
    passengerGetNearbyTaxiTypesRequest: state.requests.passengerGetNearbyTaxiTypesRequest || {},
    passengerGetNearbyTaxiTypesErrors: _.get(state.errors, 'passengerGetNearbyTaxiTypes.errors', []),

    pois: state.app.pois,
  };
}

export default connect(
  mapStateToProps,
  {
    cohortCreate,
    cohortGet,
    cohortDelete,

    unsetCohort,

    passengerBookingCreate,

    driverSetLocation,
    driverSetLocationInstant,

    driverDrag,

    unsetActiveBooking,
    unsetNewlyCreatedBooking,
    resetErrorState,

    passengerGetNearbyTaxiTypes,
    reverseGeo,
  }
)(DashboardContainer);



/** WEBPACK FOOTER **
 ** ./src/js/views/dashboard/DashboardContainer.jsx
 **/